/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { memo, useCallback, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useSelector } from 'react-redux';

import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSubscriberMobile,
} from '@modules/internationalization/intSelectors';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { Table, useTableListApi, useTableMetaApi } from '@modules/table';
import withErrorBoundaryHoc from '@modules/table/components/withErrorBoundaryHoc';
import { tableIsErrorSelector, tableIsLoadingSelector } from '@modules/table/selectors';

import { makeSelectAuthSearchSettingsMsisdnFilterMaxSize } from '../../PageAuth/selectors/authSelectors';
import {

    tableReportsSubscriberListenerMiddleware, tableReportsSubscriberListenerStart,
} from '../middleware/tableReportsSubscriberListenerMiddleware';
import { TableReportsSubscriberColumns } from './TableReportsSubscriberColumns';
import { TableReportsSubscriberFooter } from './TableReportsSubscriberFooter';

const TableReportsSubscriber = () => {

    const tSubscriber = useSelector(makeSelectTranslationPageSubscriberMobile());
    const tCommon = useSelector(makeSelectTranslationCommon());

    /* Получение meta таблицы */
    const isMultiSearchEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.TableMsisdnMultiSearch));

    const msisdnFilterMaxSize = useSelector(makeSelectAuthSearchSettingsMsisdnFilterMaxSize);

    const config = useMemo(() => TableReportsSubscriberColumns(
        tSubscriber,
        tCommon,
        isMultiSearchEnabled,
        msisdnFilterMaxSize,
    ), [isMultiSearchEnabled, msisdnFilterMaxSize, tCommon, tSubscriber]);

    const { columns } = useTableMetaApi(config);

    useEffect(() => {
        tableReportsSubscriberListenerStart(tCommon);
        return () => {
            tableReportsSubscriberListenerMiddleware.clearListeners();
        };
    }, [tCommon]);

    // /* Получение списка таблицы */
    const { data, isCompact, infiniteLoader } = useTableListApi();

    /* Table Statuses  */
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);

    const renderFooter = useCallback((HorScroll: ReactNode) => (
        <>
            {HorScroll}
            <TableReportsSubscriberFooter />
        </>
    ), []);

    if (columns.length < 2) return null;

    return (
        <Table
            columns={columns}
            data={data}
            dataName='reports-subscriber-edit'
            dataNameRow='msisdn'
            footer={renderFooter}
            infiniteScroll={infiniteLoader}
            isCompact={isCompact}
            isError={isError}
            isLoading={isLoading}
        />
    );
};

export default memo(withErrorBoundaryHoc(TableReportsSubscriber));
